const PERMISSION_CACHE_PREFIX = 'permissions.byRole.';

const getCacheKey = (roleId: string) => `${PERMISSION_CACHE_PREFIX}${encodeURIComponent(roleId)}`;

const normalizeKeys = (keys: readonly string[]) => [...new Set(keys)].sort();

const arraysEqual = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const permissionCache = {
  read(roleId: string): string[] | null {
    if (!roleId || typeof window === 'undefined') return null;

    try {
      const value: unknown = JSON.parse(window.localStorage.getItem(getCacheKey(roleId)) ?? 'null');
      if (!Array.isArray(value) || !value.every((key) => typeof key === 'string')) return null;
      return normalizeKeys(value);
    } catch {
      return null;
    }
  },

  writeIfChanged(roleId: string, keys: readonly string[]): boolean {
    if (!roleId || typeof window === 'undefined') return false;

    const normalizedKeys = normalizeKeys(keys);
    const cachedKeys = this.read(roleId);
    if (cachedKeys && arraysEqual(cachedKeys, normalizedKeys)) return false;

    try {
      window.localStorage.setItem(getCacheKey(roleId), JSON.stringify(normalizedKeys));
      return true;
    } catch {
      return false;
    }
  },

  remove(roleId: string): void {
    if (!roleId || typeof window === 'undefined') return;

    try {
      window.localStorage.removeItem(getCacheKey(roleId));
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  },

  clear(): void {
    if (typeof window === 'undefined') return;

    try {
      const permissionKeys = Array.from({ length: window.localStorage.length }, (_, index) =>
        window.localStorage.key(index),
      ).filter((key): key is string => key?.startsWith(PERMISSION_CACHE_PREFIX) ?? false);

      permissionKeys.forEach((key) => window.localStorage.removeItem(key));
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  },
};
